const AUTOSAVE_CLIENT_ID_KEY = "hw.autosave.client_id";
const AUTOSAVE_SEQ_KEY = "hw.autosave.seq";
const AUTOSAVE_SEQ_LOCK = "hw.autosave.seq.allocate";

const resolveStorage = () => {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
};

const resolveLocks = () => {
  try {
    return typeof navigator === "undefined" ? null : navigator.locks;
  } catch {
    return null;
  }
};

const createPageClientId = () =>
  `${Date.now().toString(36)}${Math.random()
    .toString(36)
    .slice(2, 10)}`;

const getOrCreatePersistedClientId = (storage, pageClientId) => {
  try {
    const storedClientId = storage.getItem(AUTOSAVE_CLIENT_ID_KEY);
    if (storedClientId) {
      return storedClientId;
    }

    storage.setItem(AUTOSAVE_CLIENT_ID_KEY, pageClientId);
    return storage.getItem(AUTOSAVE_CLIENT_ID_KEY) || pageClientId;
  } catch {
    return pageClientId;
  }
};

/**
 * Creates an autosave sequence allocator.
 *
 * Web Locks serializes the localStorage read/increment/write across tabs and
 * allows a shared persisted client_id. When locks are unavailable (or sequence
 * storage fails), this instance uses a page-specific client_id and an in-memory
 * counter so duplicate sequences become explicit cross-client conflicts.
 *
 * @returns {{ allocate: (scope: string) => Promise<number>, getClientId: () => string }}
 */
export const createAutosaveSequenceAllocator = ({
  storage = resolveStorage(),
  locks = resolveLocks(),
  now = () => Date.now(),
  createClientId = createPageClientId
} = {}) => {
  const hasLocks = Boolean(locks && typeof locks.request === "function");
  const pageClientId = createClientId();
  let clientId = hasLocks && storage ? getOrCreatePersistedClientId(storage, pageClientId) : pageClientId;

  const fallbackSequences = new Map();
  let fallbackQueue = Promise.resolve();

  const usePageClientId = () => {
    clientId = pageClientId;
  };

  const allocateFallback = scope => {
    usePageClientId();
    const previous = fallbackSequences.get(scope) || 0;
    const next = Math.max(previous + 1, now());
    fallbackSequences.set(scope, next);
    return next;
  };

  const allocateFromStorage = scope => {
    if (!storage) {
      return allocateFallback(scope);
    }

    try {
      const stored = Number(storage.getItem(AUTOSAVE_SEQ_KEY) || 0);
      const next = Math.max(stored + 1, now());
      storage.setItem(AUTOSAVE_SEQ_KEY, String(next));
      fallbackSequences.set(scope, next);
      return next;
    } catch {
      return allocateFallback(scope);
    }
  };

  const allocate = scope => {
    if (hasLocks) {
      return locks.request(AUTOSAVE_SEQ_LOCK, () => allocateFromStorage(scope));
    }

    // No cross-tab atomicity: stay on the page-specific client_id and never
    // share the sequence key across tabs.
    const allocation = fallbackQueue.then(() => allocateFallback(scope));
    fallbackQueue = allocation.catch(() => undefined);
    return allocation;
  };

  return {
    allocate,
    getClientId: () => clientId
  };
};

const autosaveSequenceAllocator = createAutosaveSequenceAllocator();

export const allocateAutosaveSequence = autosaveSequenceAllocator.allocate;
export const getAutosaveClientId = autosaveSequenceAllocator.getClientId;

export default allocateAutosaveSequence;
